/**
 * Subscription / Plan / Payment Hooks
 *
 * Hooks de React Query para que el SUPER_ADMIN gestione planes,
 * suscripciones y pagos de las IPS desde el dashboard.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { tokenStorage } from '@/lib/api'
import * as subscriptionService from './subscription.service'
import type {
  AssignSubscriptionInput,
  ExtendTrialInput,
  RecordPaymentInput,
  SuspendSubscriptionInput,
  UpdatePlanPricingInput,
  Plan,
} from '@/domain/subscription/types'

// =============================================================================
// Query keys
// =============================================================================

export const subscriptionKeys = {
  all: ['subscriptions'] as const,
  plans: () => [...subscriptionKeys.all, 'plans'] as const,
  byInstitution: (institutionId: string) =>
    [...subscriptionKeys.all, 'institution', institutionId] as const,
  payments: (institutionId: string) =>
    [...subscriptionKeys.all, 'payments', institutionId] as const,
}

// =============================================================================
// Plans
// =============================================================================

export function usePlans() {
  const hasToken = !!tokenStorage.getAccessToken()

  return useQuery({
    queryKey: subscriptionKeys.plans(),
    queryFn: () => subscriptionService.listPlans(),
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
  })
}

export function useUpdatePlan() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: { id: string; data: Partial<Plan> }) =>
      subscriptionService.updatePlan(params.id, params.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.plans() })
    },
  })
}

export function useUpdatePlanPricing() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: { id: string; data: UpdatePlanPricingInput }) =>
      subscriptionService.updatePlanPricing(params.id, params.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.plans() })
    },
  })
}

// =============================================================================
// Subscriptions
// =============================================================================

export function useInstitutionSubscription(institutionId: string | null) {
  const hasToken = !!tokenStorage.getAccessToken()

  return useQuery({
    queryKey: institutionId
      ? subscriptionKeys.byInstitution(institutionId)
      : ['subscriptions', 'institution', 'noop'],
    queryFn: () => subscriptionService.getInstitutionSubscription(institutionId as string),
    enabled: hasToken && !!institutionId,
    retry: (failureCount, error: unknown) => {
      // No reintentar si es 404 (la IPS aun no tiene suscripcion)
      const status = (error as { statusCode?: number; status?: number })?.statusCode ??
        (error as { statusCode?: number; status?: number })?.status
      if (status === 404) return false
      return failureCount < 2
    },
  })
}

export function useAssignSubscription() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: { institutionId: string; data: AssignSubscriptionInput }) =>
      subscriptionService.assignSubscription(params.institutionId, params.data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: subscriptionKeys.byInstitution(variables.institutionId),
      })
      queryClient.invalidateQueries({ queryKey: ['institutions'] })
    },
  })
}

export function useSuspendSubscription(institutionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: { subscriptionId: string; data: SuspendSubscriptionInput }) =>
      subscriptionService.suspendSubscription(params.subscriptionId, params.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: subscriptionKeys.byInstitution(institutionId),
      })
    },
  })
}

export function useReactivateSubscription(institutionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (subscriptionId: string) =>
      subscriptionService.reactivateSubscription(subscriptionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: subscriptionKeys.byInstitution(institutionId),
      })
    },
  })
}

export function useExtendTrial(institutionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: { subscriptionId: string; data: ExtendTrialInput }) =>
      subscriptionService.extendTrial(params.subscriptionId, params.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: subscriptionKeys.byInstitution(institutionId),
      })
    },
  })
}

// =============================================================================
// Payments
// =============================================================================

export function usePayments(institutionId: string | null) {
  const hasToken = !!tokenStorage.getAccessToken()

  return useQuery({
    queryKey: institutionId
      ? subscriptionKeys.payments(institutionId)
      : ['subscriptions', 'payments', 'noop'],
    queryFn: () => subscriptionService.listPayments(institutionId as string),
    enabled: hasToken && !!institutionId,
  })
}

export function useRecordPayment(institutionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: RecordPaymentInput) =>
      subscriptionService.recordPayment(institutionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.payments(institutionId) })
      queryClient.invalidateQueries({
        queryKey: subscriptionKeys.byInstitution(institutionId),
      })
    },
  })
}
